import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { ApplicationState, AppState } from '../../../store';
import { withSnackbar, ProviderContext } from 'notistack';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import {
  Tooltip,
  Box,
  IconButton,
  Grid,
  Card,
  TextField,
  CircularProgress
} from '@material-ui/core';
import { ScaleLoader } from 'react-spinners';
import { RouteComponentProps } from 'react-router';
import Autocomplete from '@material-ui/lab/Autocomplete';
import MUIDataTable, {
  SelectableRows,
  MUIDataTableMeta,
  MUIDataTableColumnOptions
} from 'mui-datatables';
import { MUITranslations } from '../../../helpers/MUITableTranslations';
import { ICaseService } from '../../../services/Interfaces/ICaseService';
import moment from 'moment';
import { CloudDownloadOutlined } from '@material-ui/icons';
import DeleteOutlineTwoToneIcon from '@material-ui/icons/DeleteOutlineTwoTone';
import { Model, RepoVehicleDto, RepoVehicleFilterRequestDto } from '../../../interfaces/Case';
import { OrganizationInfoDto } from '../../../interfaces/Organization';
import { IReferential } from '../../../interfaces/IReferential';
import { IReferentialService } from '../../../services/Interfaces/IReferentialService';
import { LanguageCode, ReferentialCode } from '../../../helpers/Constants';
import Referentials from '../../../helpers/Referentials.json';
import FileSaver from 'file-saver';

interface IRepoVQComponentState {
  repoVehicles: RepoVehicleDto[];
  organizations: OrganizationInfoDto[];
  selectedOrganizations: OrganizationInfoDto[];
  brands: IReferential[];
  selectedBrand: IReferential | null;
  models: Model[];
  selectedModels: Model[];
  vin: string;
  isLoading: boolean;
  isExporting: boolean;
  page: number;
  pageSize: number;
  count: number;
}
type IRepoVQComponentProps = { appState: AppState } & ProviderContext &
  RouteComponentProps<{ id: string; workflowId: string }>;
class RepoVQComponent extends React.PureComponent<IRepoVQComponentProps, IRepoVQComponentState> {
  private translatorService!: ITranslatorService;
  private caseService!: ICaseService;
  private referentialService!: IReferentialService;
  static contextType = ApplicationContext;

  state = {
    repoVehicles: [] as RepoVehicleDto[],
    organizations: [] as OrganizationInfoDto[],
    selectedOrganizations: [] as OrganizationInfoDto[],
    brands: [] as IReferential[],
    selectedBrand: null,
    models: [] as Model[],
    selectedModels: [] as Model[],
    vin: '',
    isLoading: false,
    isExporting: false,
    page: 0,
    pageSize: 10,
    count: 0
  } as IRepoVQComponentState;

  public componentDidMount() {
    this.setState(
      {
        isLoading: true
      },
      async () => {
        await this.loadData();
      }
    );
  }

  private buildFilter = (page: number, pageSize: number, paginate: boolean): RepoVehicleFilterRequestDto => {
    return {
      organizationIds: this.state.selectedOrganizations.map((item) => item.id),
      brands: this.state.selectedBrand ? [this.state.selectedBrand.displayName] : [],
      models: this.state.selectedModels.map((item) => item.description),
      vin: this.state.vin || null,
      page: page + 1,
      pageSize: pageSize,
      paginate: paginate,
      language:
        this.props.appState.language == LanguageCode.RO ? LanguageCode.RO : LanguageCode.EN
    } as RepoVehicleFilterRequestDto;
  };

  private loadModelsForBrand = async (brand: IReferential | null): Promise<Model[]> => {
    if (!brand) {
      return [];
    }
    return await this.caseService.GetCarModelsByBrand(brand.id);
  };

  loadData = async () => {
    const refCarBrand = Referentials.referential.find(
      (item) => item.code === ReferentialCode.CarBrands
    );

    const [brands, filters, response] = await Promise.all([
      this.referentialService.Get(refCarBrand!.baseUrl),
      this.caseService.GetRepoVehicleFilters(this.buildFilter(0, this.state.pageSize, false)),
      this.caseService.GetRepoVehicles(this.buildFilter(this.state.page, this.state.pageSize, true))
    ]);

    this.setState({
      repoVehicles: response.vehicles,
      count: response.total,
      page: response.page - 1,
      organizations: filters.organizations,
      brands: brands as IReferential[],
      isLoading: false
    });
  };

  reloadRepoVehicles = async (page: number, pageSize: number) => {
    this.setState({ isLoading: true });

    const response = await this.caseService.GetRepoVehicles(this.buildFilter(page, pageSize, true));

    this.setState({
      repoVehicles: response.vehicles,
      count: response.total,
      page: response.page - 1,
      pageSize: pageSize,
      isLoading: false
    });
  };

  handleOrganizationsChange = (newValue: OrganizationInfoDto[]) => {
    this.setState(
      {
        selectedOrganizations: newValue,
        isLoading: true,
        page: 0
      },
      async () => await this.reloadRepoVehicles(0, this.state.pageSize)
    );
  };

  handleBrandChange = (newValue: IReferential | null) => {
    this.setState({ selectedBrand: newValue, isLoading: true, page: 0 }, async () => {
      const models = await this.loadModelsForBrand(newValue);
      const modelIds = new Set(models.map((item) => item.id));
      this.setState(
        {
          models: models,
          selectedModels: this.state.selectedModels.filter((item) => modelIds.has(item.id))
        },
        async () => await this.reloadRepoVehicles(0, this.state.pageSize)
      );
    });
  };

  handleModelsChange = (newValue: Model[]) => {
    this.setState(
      {
        selectedModels: newValue,
        isLoading: true,
        page: 0
      },
      async () => await this.reloadRepoVehicles(0, this.state.pageSize)
    );
  };

  handleVinChange = (value: string) => {
    this.setState(
      {
        vin: value,
        isLoading: true,
        page: 0
      },
      async () => await this.reloadRepoVehicles(0, this.state.pageSize)
    );
  };

  handleExport = async () => {
    this.setState({ isExporting: true });
    try {
      const blob = await this.caseService.GetRepoVehiclesExport(
        this.buildFilter(this.state.page, this.state.pageSize, false)
      );
      FileSaver.saveAs(
        blob,
        `${this.translatorService.Tranlate('REPO_VQ_EXPORT_FILE_NAME', 'VQ Repo')}.xlsx`
      );
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    } finally {
      this.setState({ isExporting: false });
    }
  };

  renderFilters = () => {
    return (
      <div className="mt-3">
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box mb={2}>
              <Autocomplete
                id="organizations"
                size="small"
                multiple
                options={this.state.organizations}
                value={this.state.selectedOrganizations}
                onChange={(e: any, newValue: any) => this.handleOrganizationsChange(newValue)}
                getOptionLabel={(option: OrganizationInfoDto) => option.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="organizations"
                    label={this.translatorService.Tranlate(
                      'REPO_VQ_ORGANIZATION_FILTER',
                      'Organizatie'
                    )}
                    fullWidth
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box mb={2}>
              <Autocomplete
                id="brands"
                size="small"
                options={this.state.brands}
                value={this.state.selectedBrand}
                onChange={(e: any, newValue: any) => this.handleBrandChange(newValue)}
                getOptionLabel={(option: IReferential) => option.displayName || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="brands"
                    label={this.translatorService.Tranlate('REPO_VQ_BRAND_FILTER', 'Marca')}
                    fullWidth
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box mb={2}>
              <Autocomplete
                id="models"
                size="small"
                multiple
                options={this.state.models}
                value={this.state.selectedModels}
                onChange={(e: any, newValue: any) => this.handleModelsChange(newValue)}
                getOptionLabel={(option: Model) => option.description || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="models"
                    label={this.translatorService.Tranlate('REPO_VQ_MODEL_FILTER', 'Model')}
                    fullWidth
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box mb={2}>
              <TextField
                id="vin"
                size="small"
                value={this.state.vin}
                onChange={(e: any) => this.handleVinChange(e.target.value)}
                label={this.translatorService.Tranlate('VEHICLE_VQ_REQUERY_VIN_LABEL', 'VIN')}
                fullWidth
              />
            </Box>
          </Grid>
        </Grid>
      </div>
    );
  };

  getColumns = () => {
    const columns = [
      {
        name: 'id',
        options: { display: 'excluded', filter: false } as MUIDataTableColumnOptions
      },
      {
        name: 'vin',
        label: this.translatorService.Tranlate('VEHICLE_VQ_REQUERY_VIN_LABEL', 'VIN')
      },
      {
        name: 'make',
        label: this.translatorService.Tranlate('OIL_VISCOSITY_MAKE_CORRESPONDENCE_CAR_MAKE_LABEL', 'Marca')
      },
      {
        name: 'model',
        label: this.translatorService.Tranlate('REPO_VQ_MODEL', 'Model')
      },
      {
        name: 'umc',
        label: this.translatorService.Tranlate('REPO_VQ_UMC', 'UMC')
      },
      {
        name: 'organization',
        label: this.translatorService.Tranlate('REPO_VQ_ORGANIZATION_FILTER', 'Organizatie'),
        options: {
          sort: false,
          customBodyRender: (value: OrganizationInfoDto) => (value ? value.name : '')
        } as MUIDataTableColumnOptions
      },
      {
        name: 'dateCreation',
        label: this.translatorService.Tranlate('REPO_VQ_DATE_CREATION', 'Data creare'),
        options: {
          filter: false,
          customBodyRender: (value: Date) =>
            value ? moment.utc(value).local().format(this.props.appState.longDateFormat) : ''
        } as MUIDataTableColumnOptions
      },
      {
        name: 'dateModification',
        label: this.translatorService.Tranlate('USERS_DATE_MODIFICATION_HEADER', 'Data Modificare'),
        options: {
          filter: false,
          customBodyRender: (value: Date) =>
            value ? moment.utc(value).local().format(this.props.appState.longDateFormat) : ''
        } as MUIDataTableColumnOptions
      },
      {
        name: '',
        options: {
          viewColumns: false,
          filter: false,
          setCellHeaderProps: () => ({ align: 'center' }),
          setCellProps: () => ({ align: 'center' }),
          customBodyRender: (value: any, tableMeta: MUIDataTableMeta) => {
            return (
              <IconButton
                aria-label="delete"
                color="inherit"
                className="text-danger"
                size="small"
                onClick={(e) => this.handleDelete(tableMeta.rowData[0])}
              >
                <DeleteOutlineTwoToneIcon />
              </IconButton>
            );
          }
        }
      }
    ];
    return columns;
  };

  handleDelete = async (id: number) => {
    try {
      await this.caseService.DeleteVqRepoDataById(id);
      await this.reloadRepoVehicles(this.state.page, this.state.pageSize);
      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.caseService = (this.context as AppContext).caseService;
    this.referentialService = (this.context as AppContext).referentialService;

    const options = {
      filter: false,
      search: false,
      sort: false,
      viewColumns: false,
      selectableRows: 'none' as SelectableRows,
      selectableRowsOnClick: false,
      print: false,
      download: false,
      textLabels: MUITranslations.GetTranslations(this.translatorService),
      elevation: 0,
      serverSide: true,
      count: this.state.count,
      rowsPerPage: this.state.pageSize,
      page: this.state.page,
      onTableChange: async (action: any, tableState: any) => {
        switch (action) {
          case 'changePage':
            await this.reloadRepoVehicles(tableState.page, tableState.rowsPerPage);
            break;
          case 'changeRowsPerPage':
            await this.reloadRepoVehicles(tableState.page, tableState.rowsPerPage);
            break;
          default:
        }
      },
      customToolbar: () => {
        return (
          <Fragment>
            {this.state.isExporting ? (
              <CircularProgress
                size={20}
                color="primary"
                style={{ verticalAlign: 'middle', marginRight: 4 }}
              />
            ) : null}
            <Tooltip title={this.translatorService.Tranlate('Export', 'Exporta')}>
              <span>
                <IconButton
                  aria-label="export"
                  color="primary"
                  disabled={this.state.isExporting}
                  onClick={(e) => this.handleExport()}
                >
                  <CloudDownloadOutlined />
                </IconButton>
              </span>
            </Tooltip>
          </Fragment>
        );
      }
    };

    return (
      <Fragment>
        <Card>
          <div
            style={{
              backgroundColor: 'var(--primary)',
              fontWeight: 500,
              borderBottom: '4px solid var(--primary-light)'
            }}
            className="card-header text-white text-center"
          >
            <span className="mb-0 py-2">
              {this.translatorService.Tranlate('REPO_VQ_TITLE', 'VQ Repo')}
            </span>
          </div>
          <div className="ml-3 mr-3">{this.renderFilters()}</div>
          <Box m={1} p={1}>
            <div className="d-flex flex-row text-center flex-wrap justify-content-center">
              <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
            </div>
            {!this.state.isLoading ? (
              <MUIDataTable
                title={''}
                data={this.state.repoVehicles}
                columns={this.getColumns()}
                options={options}
              />
            ) : null}
          </Box>
        </Card>
      </Fragment>
    );
  }
}
export default connect(
  (state: ApplicationState) => ({
    appState: state.app
  }),
  null
)(withSnackbar(RepoVQComponent as any));
